import type { QueryKey } from '$lib/api/tanstack-query';

import type { DeleteOrganizationMemberRequest } from '@viamrobotics/web-client/app/v1/app';

import { getLocationQueryKeys } from '../locations';
import { moduleKeys } from '../modules';
import { permissionKeys } from '../permissions';

const base = ['organization'] as const;

export const orgKeys = {
  get: (organizationID: string | undefined) =>
    [...base, { organizationID }] as const,
  list: () => [...base, 'list'] as const,
  listForLocation: (locationID: string | undefined) =>
    [...orgKeys.list(), { locationID }] as const,
  membersAndInvites: (organizationID: string | undefined) =>
    [...orgKeys.get(organizationID), 'membersAndInvites'] as const,
  byUser: (userID: string | undefined) =>
    [...base, 'by-user', { userID }] as const,
  searchByCrmId: (crmId: string | undefined) =>
    [...base, 'search-by-crm', { crmId }] as const,
};

/** Returns a list of relevant org query keys. Useful for invalidation. */
export const getOrgQueryKeys = (): readonly QueryKey[] => {
  return [orgKeys.list()];
};

/**
 *  Returns queries to invalidate given the input parameters for DeleteOrganizationMember
 *  @param appUserID the user ID of the current app user
 *  @param request the request of the call to DeleteOrganizationMember
 *  @returns a list of query keys to invalidate
 */
export const getInvalidatedQueriesForDeleteMember = (
  appUserID: string,
  {
    userId: removedUserID,
    organizationId: orgID,
  }: DeleteOrganizationMemberRequest
): { invalidate: QueryKey[]; remove: QueryKey[] } => {
  if (removedUserID === appUserID) {
    return getInvalidatedQueriesForDeleteOrg(orgID);
  }

  return {
    invalidate: [
      orgKeys.membersAndInvites(orgID),
      permissionKeys.authorizations(orgID),
    ],
    remove: [],
  };
};

/**
 *  Returns queries to invalidate given the input parameters for DeleteOrganization
 *  @param request the request of the call to DeleteOrganization
 *  @returns a list of query keys to invalidate
 */
export const getInvalidatedQueriesForDeleteOrg = (
  orgID: string
): { invalidate: QueryKey[]; remove: QueryKey[] } => {
  const invalidate = [orgKeys.list(), permissionKeys.all()];
  const remove = [
    orgKeys.get(orgID),
    permissionKeys.authorizations(orgID),
    moduleKeys.list(orgID),
    ...getLocationQueryKeys({ organizationID: orgID }),
    ['features', orgID],
    ['listKeys', orgID],
  ];

  return { invalidate, remove };
};
